import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  #buyButtonsObserver;
  #footerObserver;
  #chatObserver;
  #abortController;
  #footerVisible = false;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');
    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    window.addEventListener('resize', this.#refreshVisibility, { signal });
    this.#setupObservers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#chatObserver?.disconnect();
    this.#abortController?.abort();
  }

  #setupObservers() {
    const form = this.#getProductForm();
    const buyButtons = form?.closest('.buy-buttons-block');
    if (!buyButtons) return;

    this.#buyButtonsObserver = new IntersectionObserver(this.#refreshVisibility);
    this.#buyButtonsObserver.observe(buyButtons);

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (footer) {
      this.#footerObserver = new IntersectionObserver(([entry]) => {
        this.#footerVisible = Boolean(entry?.isIntersecting);
        this.#refreshVisibility();
      });
      this.#footerObserver.observe(footer);
    }

    const chat = document.querySelector('shopify-chat');
    if (chat) {
      this.#chatObserver = new MutationObserver(this.#refreshVisibility);
      this.#chatObserver.observe(chat, { attributes: true, attributeFilter: ['open'] });
    }
    this.#refreshVisibility();
  }

  #refreshVisibility = () => {
    const form = this.#getProductForm();
    const buyButtons = form?.closest('.buy-buttons-block');
    if (!buyButtons) return;
    const rect = buyButtons.getBoundingClientRect();
    const buttonsVisible = rect.top < window.innerHeight && rect.bottom > 0;
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const chatOpen = Boolean(document.querySelector('shopify-chat[open], #chat-drawer[open]'));
    const visible = !buttonsVisible && !this.#footerVisible && !chatOpen && (mobile || rect.bottom < 0);
    this.refs.stickyBar.dataset.stuck = String(visible);
    this.refs.stickyBar.inert = !visible;
  };

  handleAddToCartClick = () => {
    const section = this.closest('.shopify-section');
    const details = section?.querySelector('.product-details');
    const target = details?.querySelector('variant-picker, product-custom-property-component, .buy-buttons-block');
    if (!target) return;
    const control = target.querySelector('input:checked, select, input:not([type="hidden"]):not([disabled]), textarea, button:not([disabled])');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reducedMotion ? 'instant' : 'smooth', block: 'start' });
    if (control instanceof HTMLElement) control.focus({ preventScroll: true });
  };

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      .then(({ detail }) => {
        if (!this.isConnected || !detail?.html) return;
        if (detail.productId && String(detail.productId) !== this.dataset.productId) return;
        const updated = detail.html.querySelector('sticky-add-to-cart');
        const updatedBar = updated?.querySelector('[ref="stickyBar"]');
        if (!updatedBar) return;
        morph(this.refs.stickyBar, updatedBar, { childrenOnly: true });
        this.dataset.variantAvailable = updated.dataset.variantAvailable;
        this.dataset.currentVariantId = updated.dataset.currentVariantId;
        this.#refreshVisibility();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Product update failed:', error);
      });
  };

  #getProductForm() {
    const section = this.closest('.shopify-section');
    return section?.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
